import { BonusType } from '@/webgl/runner/entities/BonusEntity'
import { CollectibleData, CollectibleType } from '@/webgl/runner/entities/CollectibleEntity'
import { CollectibleResourceTextValues, CollectibleResourceType } from '@/webgl/runner/rendering/CollectibleRenderer'
import { ScenarioSetup } from '@/webgl/runner/scenarios/ScenarioSetup.types'
import { SequenceAlternativeMode, SequenceViewVariant } from '@/webgl/runner/scenarios/SequenceConfig.types'

const { Standard, Obstacle, TurningPoint, Bonus } = CollectibleType
const { Text, Pack } = CollectibleResourceType

export const CARIS_1_SCROLL_STD = [
  'sharpener',
  'apple',
  'books',
  'headphones',
  // "papers",
  'pen_01',
  'pen_02',
  'pen_pot',
  'pencil_case',
]

export const CARIS_1_SCROLL_MASK_WORLD = [
  'mask_pot',
  'mask_01',
  'mask_02',
  'mask_apple',
  'mask_books',
  'mask_headphones',
  'mask_pencil_case',
]

const IDLE_TIME = 2.0

function generateCollectibleTexts(texts: string[]) {
  const values: CollectibleResourceTextValues[] = []
  const items: CollectibleData[] = []
  for (const text of texts) {
    values.push({
      id: text,
      content: text,
    })
    items.push({
      id: text,
      type: Standard,
    })
  }

  return {
    values,
    items,
  }
}

const TEXTS = generateCollectibleTexts([
  'e=i',
  'e=o',
  'm=n',
  'p=q',
  'b=d',

  'con',
  'khan',
  'khans',
  'const',
  'sti',
  'stit',
  'stuh',
  'stitt',
  'tu',
  'too',
  'tuu',
  'to',
  'tion',
  'stan',
  'shun',
  'shin',
  'di',
  'dip',
  'dih',
  'duh',
  'plo',
  'ploh',
  'plow',
  'plu',
  'ma',
  'muh',
  'maa',
  'mah',
  'cy',
  'cie',
  'cee',
  'see',
  'sy',
  'or',
  'oar',
  'org',
  'ore',
  'ga',
  'guh',
  'gah',
  'gu',
  'ni',
  'nih',
  'nuh',
  'ne',
  'za',
  'zay',
  'zai',
  'zi',
  'tion',
  'shun',
  'sion',
  'shon',
  'shin',
  'in',
  'en',
  'inn',
  'inf',
  'inh',
  'fra',
  'frah',
  'fraw',
  'fru',
  'struc',
  'struck',
  'struk',
  'straw',
  'ture',
  'chur',
  'chir',
  'cher',

  // // con sti tu tion
  // "con",
  // "sti",
  // "tu",
  // "tion",
  // "don",
  // "pon",
  // "stl",
  // "spi",
  // "du",
  // "dion",

  // // di plo ma cy
  // "di",
  // "plo",
  // "ma",
  // "cy",
  // "bi",
  // "pi",
  // "plq",
  // "dlo",
  // "na",
  // "dy",

  // // or ga ni za tion
  // "or",
  // "ga",
  // "ni",
  // "za",
  // "tion",
  // "op",
  // "oq",
  // "pa",
  // "qa",
  // "nl",
  // "mi",

  // // in fra struc ture
  // "in",
  // "fra",
  // "struc",
  // "ture",
  // "ln",
  // "fpa",
  // "strnc",
  // "turo",

  // Dictation
  // "the",
  // "specific",
  // "to",
  // "avoid",
  // "pacific",
  // "coal",
  // "delegacy",

  'the',
  'thee',
  'this',
  'that',
  'a',
  'specific',
  'pacific',
  'pocific',
  'particular',
  'goal',
  'coel',
  'gool',
  'goel',
  'of',
  'off',
  'about',
  'in',
  'diplomacy',
  'policy',
  'dialogue',
  'diplomat',
  'diplomaci',
  'is',
  'has',
  'was',
  'are',
  'have',

  'to',
  'too',
  'do',
  'though',
  'avoid',
  'avowed',
  'void',
  'avoi',
  'conflict',
  'conflikt',
  'inflict',
  'constrict',
  'and',
  'und',
  'or',
  'where',
  'foster',
  'faster',
  'poster',
  'phoster',
  'collaboration',
  'corporation',
  'cooperation',
  'colaboration',
])

function pick(collectibles: string[]) {
  const datas = collectibles.filter((t) => TEXTS.items.findIndex((p) => p.id == t) != -1)
  if (datas.length != collectibles.length) {
    const missings = collectibles.filter((t) => TEXTS.items.findIndex((p) => p.id == t) == -1)
    throw new Error(`[Caris1Setup] Collectible not found -> ${missings.join(', ')}`)
  }
  return collectibles
}

const config: ScenarioSetup = {
  backgroundColor: 0x4ea4bd,
  lanesColor: 0xfed1c8,
  lanesExtraColor: 0xfff1e3,
  lanesTextureId: 'book',

  clearColor: 0xfff4e5,
  smokeColor: 0xf7ead8,

  playerColor: 0x4ea4bd,
  playerGoodColor: 0xfce5ab,
  playerWrongColor: 0xfe8769,

  audioSfx: { path: 'sfx_audio/sfx.json' },
  audio: {
    en: {
      path: 'caris_1/audios/en/bundle_1.json',
      subtitles: 'caris_1/audios/en/bundle_1.vtt',
    },
  },

  speed: {
    min: 2.25,
    max: 3.85,
  },

  collectibles: {
    resources: [
      {
        type: Pack,
        request: 'caris_1/collectible.glb',
      },
      {
        type: Text,
        text: {
          font: 'fonts/UnderstoodSans-Heavy',
          style: {
            center: true,
            color: 0x06748d,
            outlineColor: 0xfff4e5,
            outline: 0.7,
            fontSize: 0.4,
            lineHeight: 1.3,
          },
          values: TEXTS.values,
        },
      },
    ],
    items: [
      { id: 'letter_picker', type: Bonus, bonus: BonusType.Focus },
      { id: 'journal', type: Bonus, bonus: BonusType.Focus, audio: 'TYCE-caris_Okay-deep-breath-1' },
      { id: 'palpitations', type: Obstacle, audio: 'TYCE-caris_what-is-going-on-1' },
      { id: 'headphones', type: Obstacle },
      ...TEXTS.items,
    ],
  },

  particles: {
    resources: [{ id: 'default', request: 'common/particles.glb' }],
  },

  background: {
    resources: [
      {
        id: 'background',
        request: 'caris_1/background.glb',
      },
    ],

    items: [
      { id: 'apple' },
      { id: 'books' },
      { id: 'headphones' },
      { id: 'mask_01' },
      { id: 'mask_02' },
      { id: 'mask_apple' },
      { id: 'mask_books' },
      { id: 'mask_headphones' },
      { id: 'mask_pencil_case' },
      { id: 'mask_pot' },
      // { id: "papers", side: ScrollSide.RIGHT },
      { id: 'pen_01' },
      { id: 'pen_02' },
      { id: 'pen_pot' },
      { id: 'pencil_case' },
      { id: 'sharpener' },
    ],
  },

  sequences: [
    //#region supports
    {
      id: 'supports',
      goals: [['e=o']],

      alternativeMode: SequenceAlternativeMode.Unordered,
      view: {
        variant: SequenceViewVariant.InvertLetters,
        unordered: true,
        template: 'supp{o}rts',
        extra: { id: 'social-studies-play-intro' },
        replacement: {
          o: {
            goal: 'e=o',
            inverted: 'e',
          },
        },
      },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        collectibles: pick(['e=i', 'e=o', 'm=n', 'p=q', 'b=d']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    //#endregion

    //#region policies
    {
      id: 'policies',
      goals: [['e=o']],
      alternativeMode: SequenceAlternativeMode.Unordered,
      view: {
        variant: SequenceViewVariant.InvertLetters,
        unordered: true,
        template: 'p{o}lici{e}s',
        replacement: {
          e: {
            goal: 'e=o',
            inverted: 'o',
          },
          o: {
            goal: 'e=o',
            inverted: 'e',
          },
        },
      },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        collectibles: pick(['e=i', 'e=o', 'm=n', 'p=q', 'b=d']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    //#endregion

    //#region education
    {
      id: 'education',
      goals: [['e=o', 'm=n']],
      alternativeMode: SequenceAlternativeMode.Unordered,
      stop: {
        preserveBonus: false,
      },
      view: {
        variant: SequenceViewVariant.InvertLetters,
        unordered: true,
        template: '{e}ducati{o}{n}',
        replacement: {
          e: {
            goal: 'e=o',
            inverted: 'o',
          },
          n: {
            goal: 'm=n',
            inverted: 'm',
          },
        },
      },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        collectibles: pick(['e=i', 'e=o', 'm=n', 'p=q', 'b=d']),
        barriers: ['letter_picker'],
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    //#endregion

    //#region constitution
    {
      id: 'constitution_1',
      goals: [['con', 'sti', 'tu', 'tion']],
      condition: { id: 'collected-count', count: 1 },
      collected: [[]],
      stop: { preserveCollectibles: true },
      view: { variant: SequenceViewVariant.Syllable },
      track: {
        rate: 1.0,
        variation: 1.0,
        type: 'procedural',
        idleTime: IDLE_TIME,
        collectibles: pick(['con', 'khan', 'khans', 'const']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    {
      id: 'constitution_2',
      goals: [['con', 'sti', 'tu', 'tion']],
      condition: { id: 'collected-count', count: 2 },
      collected: [['con']],
      stop: { preserveCollectibles: true },
      view: { variant: SequenceViewVariant.Syllable },
      track: {
        rate: 1.0,
        variation: 1.0,
        type: 'procedural',
        idleTime: IDLE_TIME,
        collectibles: pick(['sti', 'stit', 'stuh', 'stitt']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    {
      id: 'constitution_3',
      goals: [['con', 'sti', 'tu', 'tion']],
      condition: { id: 'collected-count', count: 3 },
      collected: [['con', 'sti']],
      stop: { preserveCollectibles: true },
      view: { variant: SequenceViewVariant.Syllable },
      track: {
        rate: 1.0,
        variation: 1.0,
        type: 'procedural',
        idleTime: IDLE_TIME,
        collectibles: pick(['tu', 'too', 'tuu', 'to']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    {
      id: 'constitution_4',
      goals: [['con', 'sti', 'tu', 'tion']],
      condition: { id: 'collected-count', count: 4 },
      collected: [['con', 'sti', 'tu']],
      view: { variant: SequenceViewVariant.Syllable },
      track: {
        rate: 1.0,
        variation: 1.0,
        type: 'procedural',
        idleTime: IDLE_TIME,
        collectibles: pick(['tion', 'stan', 'shun', 'shin']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    //#endregion

    //#region diplomacy
    {
      id: 'diplomacy_1',
      goals: [['di', 'plo', 'ma', 'cy']],
      condition: { id: 'collected-count', count: 1 },
      collected: [[]],
      stop: { preserveCollectibles: true },
      view: { variant: SequenceViewVariant.Syllable },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        rate: 1.0,
        variation: 1.0,
        collectibles: pick(['di', 'dip', 'dih', 'duh']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    {
      id: 'diplomacy_2',
      goals: [['di', 'plo', 'ma', 'cy']],
      condition: { id: 'collected-count', count: 2 },
      collected: [['di']],
      stop: { preserveCollectibles: true },
      view: { variant: SequenceViewVariant.Syllable },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        rate: 1.0,
        variation: 1.0,
        collectibles: pick(['plo', 'ploh', 'plow', 'plu']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    {
      id: 'diplomacy_3',
      goals: [['di', 'plo', 'ma', 'cy']],
      condition: { id: 'collected-count', count: 3 },
      collected: [['di', 'plo']],
      stop: { preserveCollectibles: true },
      view: { variant: SequenceViewVariant.Syllable },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        rate: 1.0,
        variation: 1.0,
        collectibles: pick(['ma', 'muh', 'maa', 'mah']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    {
      id: 'diplomacy_4',
      goals: [['di', 'plo', 'ma', 'cy']],
      collected: [['di', 'plo', 'ma']],
      condition: { id: 'collected-count', count: 4 },
      stop: { preserveCollectibles: false },
      view: { variant: SequenceViewVariant.Syllable },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        rate: 1.0,
        variation: 1.0,
        collectibles: pick(['cy', 'cie', 'cee', 'see', 'sy']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    //#endregion

    //#region organization
    {
      id: 'organization_1',
      goals: [['or', 'ga', 'ni', 'za', 'tion']],
      collected: [[]],
      view: { variant: SequenceViewVariant.Syllable },
      condition: { id: 'collected-count', count: 1 },
      stop: { preserveCollectibles: true, preserveBonus: true },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        rate: 1.0,
        variation: 1.0,
        collectibles: pick(['or', 'oar', 'org', 'ore']),
        barriers: ['journal'],
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    {
      id: 'organization_2',
      goals: [['or', 'ga', 'ni', 'za', 'tion']],
      collected: [['or']],
      view: { variant: SequenceViewVariant.Syllable },
      condition: { id: 'collected-count', count: 2 },
      stop: { preserveCollectibles: true, preserveBonus: true },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        rate: 1.0,
        variation: 1.0,
        collectibles: pick(['ga', 'guh', 'gah', 'gu']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    {
      id: 'organization_3',
      goals: [['or', 'ga', 'ni', 'za', 'tion']],
      collected: [['or', 'ga']],
      view: { variant: SequenceViewVariant.Syllable },
      condition: { id: 'collected-count', count: 3 },
      stop: { preserveCollectibles: true, preserveBonus: true },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        rate: 1.0,
        variation: 1.0,
        collectibles: pick(['ni', 'nih', 'nuh', 'ne']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    {
      id: 'organization_4',
      goals: [['or', 'ga', 'ni', 'za', 'tion']],
      collected: [['or', 'ga', 'ni']],
      view: { variant: SequenceViewVariant.Syllable },
      condition: { id: 'collected-count', count: 4 },
      stop: { preserveCollectibles: true, preserveBonus: true },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        rate: 1.0,
        variation: 1.0,
        collectibles: pick(['za', 'zay', 'zai', 'zi']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    {
      id: 'organization_5',
      goals: [['or', 'ga', 'ni', 'za', 'tion']],
      collected: [['or', 'ga', 'ni', 'za']],
      view: { variant: SequenceViewVariant.Syllable },
      condition: { id: 'collected-count', count: 5 },
      stop: { preserveCollectibles: false },
      track: {
        type: 'procedural',
        idleTime: IDLE_TIME,
        rate: 1.0,
        variation: 1.0,
        collectibles: pick(['tion', 'shun', 'sion', 'shon', 'shin']),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    //#endregion

    //#region dictation_1
    {
      id: 'dictation_1',
      goals: [['the', 'specific', 'goal', 'of', 'diplomacy', 'is']],
      goalText: 'Collect the right words in order:',
      view: {
        variant: SequenceViewVariant.Dictation,
        template: 'infrastructure',
      },
      stop: {
        preserveCollectibles: true,
      },
      condition: {
        id: 'spawned-count',
        count: 55,
      },
      track: {
        warmupTime: 0,
        rate: 1.2,
        variation: 1.25,
        type: 'procedural',
        idleTime: IDLE_TIME,
        collectibles: pick([
          'the',
          'thee',
          'this',
          'that',
          'a',
          'specific',
          'pacific',
          'pocific',
          'particular',
          'goal',
          'coel',
          'gool',
          'goel',
          'of',
          'off',
          'about',
          'in',
          'diplomacy',
          'policy',
          'dialogue',
          'diplomat',
          'diplomaci',
          'is',
          'has',
          'was',
          'are',
          'have',
        ]),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    //#endregion

    //#region dictation_2
    {
      id: 'dictation_2',
      goals: [['to', 'avoid', 'conflicts', 'and', 'faster', 'cooperation']],
      goalText: 'Collect the right words in order:',
      view: {
        variant: SequenceViewVariant.Dictation,
        template: 'infrastructure',
      },
      start: {
        barrier: 'palpitations',
      },
      stop: {
        preserveCompletion: true,
      },
      condition: {
        id: 'spawned-count',
        count: 55,
      },
      track: {
        warmupTime: 0,
        rate: 0.9,
        variation: 1,
        type: 'procedural',
        idleTime: IDLE_TIME,
        collectibles: pick([
          'to',
          'too',
          'do',
          'though',
          'avoid',
          'avowed',
          'void',
          'avoi',
          'conflict',
          'conflikt',
          'inflict',
          'constrict',
          'and',
          'und',
          'or',
          'where',
          'foster',
          'faster',
          'poster',
          'phoster',
          // "collaboration", "corporation", "cooperation", "colaboration",
        ]),
      },
      scrolling: CARIS_1_SCROLL_STD,
    },
    //#endregion

    //#region mask_world
    {
      id: 'mask_world',
      goals: [['to', 'avoid', 'conflicts', 'and', 'faster', 'cooperation']],
      goalText: 'Collect the right words in order:',
      start: {
        bonus: BonusType.Mask,
        backgroundAudios: [
          {
            audio: 'TYCE-caris_Im-so-behind-1',
            delay: 4,
          },
          {
            audio: 'TYCE-caris_why-cant-i-1',
            delay: 1,
          },
          {
            audio: 'TYCE-caris_I-want-to-remember-1',
            delay: 1,
          },
        ],
      },
      stop: {
        preserveCollectibles: true,
      },
      view: {
        variant: SequenceViewVariant.Dictation,
        template: 'infrastructure',
      },
      condition: {
        id: 'spawned-count',
        count: 55,
      },
      track: {
        rate: 0.9,
        variation: 1,
        type: 'procedural',
        idleTime: IDLE_TIME,
        collectibles: pick([
          'to',
          'too',
          'do',
          'though',
          'avoid',
          'avowed',
          'void',
          'avoi',
          'conflict',
          'conflikt',
          'inflict',
          'constrict',
          'and',
          'und',
          'or',
          'where',
          'foster',
          'faster',
          'poster',
          'phoster',
          'collaboration',
          'corporation',
          'cooperation',
          'colaboration',
        ]),
      },
      scrolling: CARIS_1_SCROLL_MASK_WORLD,
    },
    //#endregion
  ],

  turningPoints: [],

  audioSegments: [
    {
      id: 'audio_intro',
      audios: ['TYCE-caris_Social-studies-1', 'TYCE-caris_I-need-your-help-1'],
    },

    {
      id: 'audio_teacher',
      audios: ['TYCE_caris_hello-everyone-1'],
    },

    {
      id: 'audio_intro_syllable',
      audios: ['TYCE-caris_Okay-I-understand-1'],
    },

    {
      id: 'audio_dictation_1',
      audios: ['TYCE_caris_the-specific-role-1'],
    },

    {
      id: 'audio_dictation_2',
      audios: ['TYCE_caris_to-avoid-conflicts-1'],
    },

    {
      id: 'outro_bis',
      audios: ['TYCE-caris_Class-is-over-1'],
    },
  ],
}

export default config
